import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, CheckCircle2, Lock } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { fetchInvoice, money, formatDate } from "../../lib/api";
import PayPalPayButton from "../common/PayPalPayButton";
import Receipt from "../common/Receipt";

export default function PayInvoice() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);

  const handlePaid = () => {
    fetchInvoice(id).then(setInvoice).catch(() => {}).finally(() => setDone(true));
  };

  useEffect(() => {
    fetchInvoice(id)
      .then(setInvoice)
      .catch(() => toast.error("Invoice not found"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>;
  }
  if (!invoice) {
    return <div className="text-center py-20 text-gray-400">Invoice not found.</div>;
  }

  if (done || invoice.status === "paid") {
    return (
      <div className="max-w-lg mx-auto bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center" data-testid="pay-success">
        <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
        <h1 className="mt-4 font-serif text-2xl font-bold text-navy">Payment Complete</h1>
        <p className="mt-2 text-gray-600">Invoice {invoice.number} for {money(invoice.total)} has been paid. Thank you!</p>
        <div className="mt-6 flex items-center justify-center gap-2">
          <Button onClick={() => setShowReceipt(true)} variant="outline" className="border-green-200 text-green-700 hover:bg-green-50" data-testid="view-receipt-button">
            View Receipt
          </Button>
          <Button onClick={() => navigate("/portal/invoices")} className="bg-navy hover:bg-navy-light text-white">
            Back to Invoices
          </Button>
        </div>
        {showReceipt && <Receipt invoice={invoice} onClose={() => setShowReceipt(false)} />}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <Link to="/portal/invoices" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-navy mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to invoices
      </Link>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Invoice summary */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-xl font-bold text-navy">{invoice.number}</h2>
            <span className="text-xs text-gray-400">{formatDate(invoice.created_at)}</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">Billed to {invoice.client_name}</p>
          <div className="mt-5 space-y-2">
            {invoice.items?.map((it, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-gray-600">{it.description} × {it.quantity}</span>
                <span className="font-medium text-navy">{money(it.unit_price * it.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{money(invoice.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Tax</span><span>{money(invoice.tax)}</span></div>
            <div className="flex justify-between pt-1.5 border-t border-gray-100"><span className="font-semibold text-navy">Total Due</span><span className="font-serif text-xl font-bold text-navy">{money(invoice.total)}</span></div>
          </div>
        </div>

        {/* Payment */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="font-serif text-xl font-bold text-navy flex items-center gap-2">
            <Lock className="h-5 w-5 text-gold" /> Pay with PayPal
          </h2>
          <p className="text-sm text-gray-500 mt-1">Pay securely with your PayPal account or any card.</p>
          <div className="mt-5">
            <PayPalPayButton invoiceId={id} onPaid={handlePaid} />
          </div>
        </div>
      </div>
    </div>
  );
}
